import {
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  Skeleton,
  ToastProvider,
  cn
} from '@heroui/react';
import { Xmark } from 'iconoir-react';
import { useCallback, useState } from 'react';

import { AgentLogCollectPanel } from './AgentLogCollectPanel';
import { AgentLogDetailsAccordionSections } from './AgentLogDetailsAccordionSections';
import { AgentLogDetailsHeader } from './AgentLogDetailsHeader';
import { agentLogDrawerActionFooterClassName } from './agentLogDrawerAccordionStyles';
import { formatAgentLogOutput } from './formatAgentLogOutput';

import {
  AgentExecutionNotFoundError,
  isAgentExecutionNotFoundError
} from '~/api/agentLogs/errors';
import type { AgentExecutionDetail } from '~/api/agentLogs/types';
import i18n from '~/common/helpers/i18n';
import { useAgentLogCollectEnabled } from '~/scenes/Monitor/scenes/AgentLogs/hooks/useAgentLogCollectEnabled';

const AGENT_LOG_DETAILS_DRAWER_WIDTH_CLASS = 'w-[560px]';

type AgentLogDetailsDrawerShellHeaderProps = {
  onClose: () => void;
};

function AgentLogDetailsDrawerShellHeader({
  onClose
}: AgentLogDetailsDrawerShellHeaderProps) {
  return (
    <header className='flex items-center justify-between gap-2 border-b border-default-200 bg-white px-6 py-3'>
      <h2 className='flex-1 text-lg font-semibold text-gray-900'>
        {i18n.t('scenes.monitor.agentLogs.details.title')}
      </h2>
      <Button
        isIconOnly
        size='sm'
        variant='light'
        onPress={onClose}
        aria-label={i18n.t('action.close')}
        className='text-muted-foreground h-8 w-8 min-w-8 rounded-md hover:bg-default/40'
        data-testid='monitor-agent-logs-details-button-close'
      >
        <Xmark className='h-5 w-5' />
      </Button>
    </header>
  );
}

type AgentLogDetailsDrawerProps = {
  isOpen: boolean;
  realm: string;
  detail: AgentExecutionDetail | undefined;
  loading?: boolean;
  error: Error | null;
  onClose: () => void;
};

function AgentLogDetailsError({ error }: { error: Error }) {
  const message = isAgentExecutionNotFoundError(error)
    ? i18n.t('scenes.monitor.agentLogs.errors.notFound')
    : i18n.t('scenes.monitor.agentLogs.errors.generic');

  return (
    <div
      className='px-2 py-8 text-center text-body-regular-md text-error'
      data-testid='monitor-agent-logs-details-error'
    >
      {message}
    </div>
  );
}

function AgentLogDetailsLoading() {
  return (
    <div
      className='flex flex-col gap-3 px-2 py-4'
      data-testid='monitor-agent-logs-details-loading'
    >
      <Skeleton className='h-6 w-64 rounded' />
      <Skeleton className='h-4 w-full rounded' />
      <Skeleton className='h-4 w-3/4 rounded' />
      <Skeleton className='h-32 w-full rounded' />
      <Skeleton className='h-48 w-full rounded' />
    </div>
  );
}

export function AgentLogDetailsDrawer({
  isOpen,
  realm,
  detail,
  loading = false,
  error,
  onClose
}: AgentLogDetailsDrawerProps) {
  const [collectOpen, setCollectOpen] = useState(false);
  const { isEnabled: isCollectEnabled } = useAgentLogCollectEnabled();

  const handleClose = useCallback(() => {
    setCollectOpen(false);
    onClose();
  }, [onClose]);

  const handleCollectClose = useCallback(() => {
    setCollectOpen(false);
  }, []);

  const canCollect =
    isCollectEnabled &&
    detail?.canCollect &&
    detail.status !== 'RUNNING' &&
    Boolean(detail && formatAgentLogOutput(detail.output));
  const showCollectPanel = isCollectEnabled && collectOpen && detail;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      placement='right'
      hideCloseButton
      data-testid='monitor-agent-logs-details-drawer'
      classNames={{
        backdrop: 'z-[3000]',
        wrapper: 'z-[3001]',
        base: cn(
          'max-w-[min(100vw,1512px)]',
          collectOpen && isCollectEnabled
            ? 'w-full'
            : AGENT_LOG_DETAILS_DRAWER_WIDTH_CLASS
        ),
        body: 'flex min-h-0 flex-1 flex-col overflow-hidden p-0'
      }}
    >
      <ToastProvider
        placement='top-right'
        regionProps={{ className: 'z-[3100]' }}
      />
      <DrawerContent>
        <div className='flex h-full min-h-0 w-full'>
          <div
            className={cn(
              'flex h-full shrink-0 flex-col',
              AGENT_LOG_DETAILS_DRAWER_WIDTH_CLASS
            )}
          >
            <AgentLogDetailsDrawerShellHeader onClose={handleClose} />

            <DrawerBody className='flex min-h-0 flex-1 flex-col overflow-hidden px-4 py-0'>
              {loading ? <AgentLogDetailsLoading /> : null}

              {!loading && error ? (
                <AgentLogDetailsError error={error} />
              ) : null}

              {!loading && !error && detail ? (
                <div className='flex min-h-0 flex-1 flex-col gap-4 overflow-auto py-4'>
                  <AgentLogDetailsHeader detail={detail} />
                  <AgentLogDetailsAccordionSections
                    logs={detail.logs}
                    output={detail.output}
                  />
                </div>
              ) : null}

              {!loading && !error && detail && isCollectEnabled ? (
                <div
                  className={cn(
                    agentLogDrawerActionFooterClassName,
                    'mt-auto -mx-4'
                  )}
                >
                  <Button
                    color='primary'
                    onPress={() => setCollectOpen(true)}
                    isDisabled={!canCollect || collectOpen}
                    data-testid='monitor-agent-logs-details-create-test-case'
                  >
                    {i18n.t('scenes.monitor.agentLogs.collect.createTestCase')}
                  </Button>
                </div>
              ) : null}
            </DrawerBody>
          </div>

          {showCollectPanel ? (
            <AgentLogCollectPanel
              realm={realm}
              detail={detail}
              onClose={handleCollectClose}
            />
          ) : null}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

export { AgentExecutionNotFoundError };
